import React, { useEffect, useMemo, useState } from "react";
import styles from "./Cart.module.css";
import QuantityControl from "../UILibrary/QuantityControl";
import TrashIcon from "Utils/Assets/SVGs/TrashIcon";
import { ICartItemProps } from "@model/Cart.model";
import { clampItemQuantity } from "../../../Utils/Global/globalfuncion";
import useImageUploadRules from "../../../Logic/Functions/Hooks/useImageUploadRules";

const CartItemCard: React.FC<ICartItemProps> = ({
  item,
  checked,
  onToggle,
  onQtyChange,
  onDelete,
  formatMoney,
}) => {
  const MIN_QTY = 1;
  const numericStock = Number(item.stock);
  const numericUserLimit = Number(item.maxPerUser);

  const caps: number[] = [];
  if (Number.isFinite(numericStock)) caps.push(Math.max(MIN_QTY, numericStock));
  if (Number.isFinite(numericUserLimit))
    caps.push(Math.max(MIN_QTY, numericUserLimit));
  const maxAllowed: number | undefined = caps.length
    ? Math.min(...caps)
    : undefined;
  const isOutOfStock = Number.isFinite(numericStock) && numericStock <= 0;

  const [draftQty, setDraftQty] = useState<number>(() =>
    clampItemQuantity(item.qty, MIN_QTY, maxAllowed ?? Number.POSITIVE_INFINITY)
  );
  const [qtyError, setQtyError] = useState<string | null>(null);

  useEffect(() => {
    setDraftQty(
      clampItemQuantity(
        item.qty,
        MIN_QTY,
        maxAllowed ?? Number.POSITIVE_INFINITY
      )
    );
    setQtyError(null);
  }, [item.qty, maxAllowed]);

  const hasPendingChanges = draftQty !== item.qty;
  const previewLineTotal = useMemo(
    () => Number(item.price ?? 0) * draftQty,
    [item.price, draftQty]
  );

  const handlePreview = (next: number) => {
    const clamped = clampItemQuantity(
      next,
      MIN_QTY,
      maxAllowed ?? Number.POSITIVE_INFINITY
    );
    setDraftQty(clamped);

    if (typeof maxAllowed === "number" && next > maxAllowed) {
      setQtyError(`Maximum allowed quantity is ${maxAllowed}`);
    } else if (next < MIN_QTY) {
      setQtyError(`Minimum allowed is ${MIN_QTY}`);
    } else {
      setQtyError(null);
    }
  };

  const handleSave = () => {
    if (!hasPendingChanges) return;
    if (typeof maxAllowed === "number" && draftQty > maxAllowed) {
      setQtyError(`Maximum allowed quantity is ${maxAllowed}`);
      setDraftQty(maxAllowed);
      return;
    }
    if (draftQty < MIN_QTY) {
      setQtyError(`Minimum allowed is ${MIN_QTY}`);
      setDraftQty(MIN_QTY);
      return;
    }
    onQtyChange(draftQty);
  };

  const handleCancel = () => {
    setDraftQty(item.qty);
    setQtyError(null);
  };

  const { buildImageSrc } = useImageUploadRules({
    maxFiles: 1,
    maxSizeMB: 5,
    requireAtLeastOne: false,
    enforceTotalSize: false,
  });

  const normalizedImage = buildImageSrc(item.imageUrl || null) ?? "";

  return (
    <div
      className={`${styles.cartItem} ${!checked ? styles.itemDimmed : ""}`}
      data-testid="cart-item"
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        className={styles.selectBox}
        aria-label={`Select ${item.name}`}
        data-testid="cart-select"
      />

      <img src={normalizedImage} alt={item.name} className={styles.cartThumb} />

      <div className={styles.cartInfo}>
        <div className={styles.cartName} data-testid="cart-name">
          {item.name}
        </div>

        <div className={styles.cartPriceRow}>
          <span
            className={`${styles.cartPrice} ${styles.tnum}`}
            data-testid="cart-price"
          >
            {formatMoney(Number(item.price ?? 0), item.currency)}
          </span>

          <QuantityControl
            value={draftQty}
            min={MIN_QTY}
            max={maxAllowed}
            disabled={isOutOfStock}
            onChange={handlePreview}
            onPreviewChange={handlePreview}
            onError={setQtyError}
            customContainerClass={styles.qtyContainer}
            inputClass={styles.qtyInput}
            incrementButtonClass={styles.qtyBtn}
            decrementButtonClass={styles.qtyBtn}
            data-testid="cart-qty"
          />
        </div>


        {hasPendingChanges && (
          <div className={styles.editRow} data-testid="cart-edit-row">
            <button
              type="button"
              className={styles.saveBtn}
              onClick={handleSave}
              data-testid="cart-save"
            >
              Save
            </button>
            <button
              type="button"
              className={styles.resetBtn}
              onClick={handleCancel}
              data-testid="cart-cancel"
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      <div
        className={`${styles.cartLineTotal} ${styles.tnum}`}
        data-testid="cart-line-total"
      >
        {formatMoney(previewLineTotal, item.currency)}
      </div>

      <button
        type="button"
        className={styles.deleteBtn}
        onClick={onDelete}
        data-testid="cart-delete"
        aria-label={`Remove ${item.name}`}
        title="Remove"
      >
        <TrashIcon className={styles.deleteIcon} width={18} height={18} />
      </button>
    </div>
  );
};

export default CartItemCard;
